/**
 * Vue 3 Composable for Card Validation and Classification
 * Consolidates all card-related logic from vanilla JS codebase
 * Provides type-safe card operations with comprehensive validation
 */

import type { ComputedRef } from 'vue'
// ComputedRef type used in return types; computed function reserved for future use
void (0 as unknown as ComputedRef<unknown>) // Acknowledge type import

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/**
 * Card suits
 */
export type Suit = 'hearts' | 'diamonds' | 'clubs' | 'spades' | 'joker'

/**
 * Card ranks
 */
export type Rank =
  | 'A'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | 'J'
  | 'Q'
  | 'K'
  | 'JOKER'

/**
 * Card type classification
 */
export type CardType = 'natural' | 'wild' | 'red_three' | 'black_three'

/**
 * Canasta type classification
 */
export type CanastaType = 'natural' | 'mixed' | 'wild' | 'hidden_wild'

/**
 * Meld suggestion from findPotentialMelds
 */
export interface MeldSuggestion {
  rank: Rank
  naturals: Card[]
  wilds: Card[]
  totalCards: number
  points: number
  isNewMeld: boolean
  canFormCanasta: boolean
}

/**
 * Card object structure
 */
export interface Card {
  suit: Suit
  rank: Rank
  deck_index?: number
}

/**
 * Meld validation result with detailed error information
 */
export interface MeldValidationResult {
  valid: boolean
  error?: {
    title: string
    message: string
    tips: string[]
  }
}

/**
 * Meld structure
 */
export interface Meld {
  rank: Rank
  cards: Card[]
  is_wild_meld: boolean
}

/**
 * Opening meld requirements based on team score
 */
export interface OpeningMeldRequirement {
  minScore: number
  maxScore: number
  requiredPoints: number
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Opening meld point requirements based on team score
 */
export const OPENING_MELD_REQUIREMENTS: OpeningMeldRequirement[] = [
  { minScore: 0, maxScore: 2500, requiredPoints: 50 },
  { minScore: 2501, maxScore: 5000, requiredPoints: 90 },
  { minScore: 5001, maxScore: 7500, requiredPoints: 120 },
  { minScore: 7501, maxScore: Infinity, requiredPoints: 150 },
]

/**
 * Canasta bonus points by type
 */
export const CANASTA_BONUS: Record<CanastaType, number> = {
  natural: 500,
  mixed: 300,
  wild: 1000,
  hidden_wild: 1500,
}

/**
 * Minimum cards required for a canasta
 */
export const CANASTA_SIZE = 7

/**
 * Minimum cards required for a meld
 */
export const MIN_MELD_SIZE = 3

/**
 * Minimum natural cards required for a meld
 */
export const MIN_NATURAL_CARDS = 2

/**
 * Maximum wild cards allowed in a meld before canasta
 */
export const MAX_WILD_CARDS_BEFORE_CANASTA = 3

// ============================================================================
// Main Composable
// ============================================================================

/**
 * Vue 3 Composable for card validation and classification
 * Provides pure functions for card operations (no reactive state needed)
 */
export function useCards() {
  // ============================================================================
  // Card Classification Functions
  // ============================================================================

  /**
   * Check if a card is a wild card (Joker or 2)
   * @param card - Card to check
   * @returns True if card is wild (Joker or 2)
   */
  function isWild(card: Card | null | undefined): boolean {
    if (!card) return false
    const rank = String(card.rank || '').toUpperCase()
    const suit = String(card.suit || '').toLowerCase()
    return rank === 'JOKER' || suit === 'joker' || rank === '2'
  }

  /**
   * Check if a card is a natural card (meldable, not wild or dead)
   * @param card - Card to check
   * @returns True if card is natural
   */
  function isNatural(card: Card | null | undefined): boolean {
    if (!card) return false
    return !isWild(card) && !isDead(card)
  }

  /**
   * Check if a card is a dead card (any 3)
   * @param card - Card to check
   * @returns True if card is a 3
   */
  function isDead(card: Card | null | undefined): boolean {
    if (!card) return false
    const rank = String(card.rank || '').toUpperCase()
    return rank === '3'
  }

  /**
   * Check if a card is a red three
   * @param card - Card to check
   * @returns True if card is a red 3 (hearts or diamonds)
   */
  function isRedThree(card: Card | null | undefined): boolean {
    if (!card) return false
    const rank = String(card.rank || '').toUpperCase()
    const suit = String(card.suit || '').toLowerCase()
    return rank === '3' && (suit === 'hearts' || suit === 'diamonds')
  }

  /**
   * Check if a card is a black three
   * @param card - Card to check
   * @returns True if card is a black 3 (clubs or spades)
   */
  function isBlackThree(card: Card | null | undefined): boolean {
    if (!card) return false
    const rank = String(card.rank || '').toUpperCase()
    const suit = String(card.suit || '').toLowerCase()
    return rank === '3' && (suit === 'clubs' || suit === 'spades')
  }

  /**
   * Get the card type classification
   * @param card - Card to classify
   * @returns Card type (wild, natural, red_three, black_three)
   */
  function getCardType(card: Card | null | undefined): CardType | null {
    if (!card) return null
    if (isRedThree(card)) return 'red_three'
    if (isBlackThree(card)) return 'black_three'
    if (isWild(card)) return 'wild'
    return 'natural'
  }

  // ============================================================================
  // Point Calculation Functions
  // ============================================================================

  /**
   * Get the point value of a card
   * @param card - Card to evaluate
   * @returns Point value (negative for penalty cards)
   */
  function getCardPoints(card: Card | null | undefined): number {
    if (!card) return 0
    const rank = String(card.rank || '').toUpperCase()
    const suit = String(card.suit || '').toLowerCase()

    // Jokers are 50 points
    if (rank === 'JOKER' || suit === 'joker') return 50

    // 2s (wild) are 20 points
    if (rank === '2') return 20

    // Aces are 20 points
    if (rank === 'A') return 20

    // K, Q, J, 10, 9, 8 are 10 points each
    if (['K', 'Q', 'J', '10', '9', '8'].includes(rank)) return 10

    // 7, 6, 5, 4 are 5 points each
    if (['7', '6', '5', '4'].includes(rank)) return 5

    // 3s are penalty cards (dead cards)
    if (rank === '3') {
      // Red 3s = -500 pts penalty, Black 3s = -5 pts penalty
      return suit === 'hearts' || suit === 'diamonds' ? -500 : -5
    }

    return 0
  }

  /**
   * Calculate total points for an array of cards
   * @param cards - Array of cards
   * @returns Total point value
   */
  function calculateTotalPoints(cards: Card[]): number {
    return cards.reduce((total, card) => total + getCardPoints(card), 0)
  }

  /**
   * Get opening meld requirement based on team score
   * @param teamScore - Current team score
   * @returns Required points for opening meld
   */
  function getOpeningMeldRequirement(teamScore: number): number {
    const requirement = OPENING_MELD_REQUIREMENTS.find(
      (req) => teamScore >= req.minScore && teamScore <= req.maxScore
    )
    return requirement?.requiredPoints || 50
  }

  // ============================================================================
  // Meld Validation Functions
  // ============================================================================

  /**
   * Validate a meld with detailed error reporting
   * @param cards - Cards in the meld
   * @param isWildMeld - Whether this is intended to be a wild meld
   * @returns Validation result with error details if invalid
   */
  function validateMeld(
    cards: Card[],
    isWildMeld = false
  ): MeldValidationResult {
    // Check minimum cards
    if (cards.length < MIN_MELD_SIZE) {
      return {
        valid: false,
        error: {
          title: 'Not Enough Cards',
          message: 'A meld requires at least 3 cards.',
          tips: [
            'Select more cards of the same rank',
            'Add wild cards (2s or Jokers) to reach 3 cards',
          ],
        },
      }
    }

    // Check for dead cards (3s)
    const deadCards = cards.filter((c) => isDead(c))
    if (deadCards.length > 0) {
      const deadTypes = deadCards.map((c) => {
        const isRed = c.suit === 'hearts' || c.suit === 'diamonds'
        return isRed ? 'Red 3' : 'Black 3'
      })
      return {
        valid: false,
        error: {
          title: 'Dead Cards Cannot Be Melded',
          message: `3s are dead cards and cannot be used in melds. You have: ${[...new Set(deadTypes)].join(', ')}.`,
          tips: [
            'Red 3s: -500 points penalty if caught',
            'Black 3s: -5 points penalty if caught',
            'Avoid picking these up from the discard pile',
          ],
        },
      }
    }

    // Wild meld validation
    if (isWildMeld) {
      const nonWilds = cards.filter((c) => !isWild(c))
      if (nonWilds.length > 0) {
        return {
          valid: false,
          error: {
            title: 'Wild Meld Invalid',
            message:
              'A wild meld must contain only wild cards (2s and Jokers).',
            tips: [
              'Remove the natural cards from this meld',
              'Create a separate meld for natural cards',
            ],
          },
        }
      }
      return { valid: true }
    }

    // Natural meld validation
    const naturals = cards.filter((c) => !isWild(c))
    const wilds = cards.filter((c) => isWild(c))

    // Must have at least 2 naturals
    if (naturals.length < MIN_NATURAL_CARDS) {
      return {
        valid: false,
        error: {
          title: 'Not Enough Natural Cards',
          message:
            'A meld must have at least 2 natural cards of the same rank.',
          tips: [
            'Add more natural cards of the same rank',
            `You have ${naturals.length} natural${naturals.length !== 1 ? 's' : ''} and ${wilds.length} wild${wilds.length !== 1 ? 's' : ''}`,
          ],
        },
      }
    }

    // Wilds cannot exceed naturals (before canasta)
    if (cards.length < CANASTA_SIZE && wilds.length >= naturals.length) {
      return {
        valid: false,
        error: {
          title: 'Too Many Wild Cards',
          message: `Natural cards must outnumber wild cards. You have ${naturals.length} natural${naturals.length !== 1 ? 's' : ''} and ${wilds.length} wild${wilds.length !== 1 ? 's' : ''}.`,
          tips: [
            'Add more natural cards of the same rank',
            'Remove some wild cards',
            'A 7-card canasta can have up to 3 wilds with 4 naturals',
          ],
        },
      }
    }

    // Max 3 wilds before canasta
    if (cards.length < CANASTA_SIZE && wilds.length > MAX_WILD_CARDS_BEFORE_CANASTA) {
      return {
        valid: false,
        error: {
          title: 'Maximum 3 Wild Cards',
          message: `Before completing a canasta (7 cards), a meld can have at most 3 wild cards. You have ${wilds.length}.`,
          tips: [
            'Remove wild cards to have at most 3',
            'Add natural cards to complete a 7-card canasta',
          ],
        },
      }
    }

    // All naturals must be same rank
    if (naturals.length > 0 && naturals[0]) {
      const targetRank = String(naturals[0].rank).toUpperCase()
      const wrongRank = naturals.find(
        (c) => String(c.rank).toUpperCase() !== targetRank
      )
      if (wrongRank) {
        return {
          valid: false,
          error: {
            title: 'Mismatched Ranks',
            message: `All natural cards must be the same rank. Found ${targetRank} and ${String(wrongRank.rank).toUpperCase()}.`,
            tips: [
              "Remove cards that don't match",
              'Create separate melds for different ranks',
            ],
          },
        }
      }
    }

    return { valid: true }
  }

  /**
   * Check if a card can be added to an existing meld
   * @param meld - Existing meld
   * @param card - Card to add
   * @returns True if card can be added
   */
  function canAddToMeld(meld: Meld, card: Card): boolean {
    if (meld.is_wild_meld) {
      return isWild(card)
    }

    const isCanasta = meld.cards.length >= CANASTA_SIZE

    if (isWild(card)) {
      // Wild card limits only apply before canasta is completed
      if (!isCanasta) {
        const currentWilds = meld.cards.filter((c) => isWild(c)).length
        const currentNaturals = meld.cards.filter((c) => !isWild(c)).length
        const newWildCount = currentWilds + 1

        // Cannot have more than 3 wilds before canasta
        if (newWildCount > MAX_WILD_CARDS_BEFORE_CANASTA) {
          return false
        }

        // Wilds cannot equal or exceed naturals before canasta
        if (newWildCount >= currentNaturals) {
          return false
        }
      }
      return true
    } else {
      // Natural card must match rank and not be dead
      return card.rank === meld.rank && !isDead(card)
    }
  }

  // ============================================================================
  // Canasta Detection Functions
  // ============================================================================

  /**
   * Check if a meld is a canasta (7+ cards)
   * @param meld - Meld to check
   * @returns True if meld has 7 or more cards
   */
  function isCanasta(meld: Meld): boolean {
    return meld.cards.length >= CANASTA_SIZE
  }

  /**
   * Get the canasta type for a completed canasta
   * @param meld - Meld to classify
   * @returns Canasta type or null if not a canasta
   */
  function getCanastaType(meld: Meld): CanastaType | null {
    if (!isCanasta(meld)) {
      return null
    }

    if (meld.is_wild_meld) {
      // Wild canastas - hidden tracking is done separately
      return 'wild'
    }

    const wildCount = meld.cards.filter((c) => isWild(c)).length

    if (wildCount === 0) {
      return 'natural'
    } else {
      return 'mixed'
    }
  }

  /**
   * Get the bonus points for a canasta
   * @param meld - Canasta meld
   * @returns Bonus points (0 if not a canasta)
   */
  function getCanastaBonus(meld: Meld): number {
    const type = getCanastaType(meld)
    return type ? CANASTA_BONUS[type] : 0
  }

  /**
   * Count canastas by type in a list of melds
   * @param melds - Array of melds
   * @returns Object with counts for each canasta type
   */
  function countCanastas(melds: Meld[]): {
    natural: number
    mixed: number
    wild: number
    total: number
  } {
    const counts = {
      natural: 0,
      mixed: 0,
      wild: 0,
      total: 0,
    }

    melds.forEach((meld) => {
      const type = getCanastaType(meld)
      if (type === 'natural') {
        counts.natural++
        counts.total++
      } else if (type === 'mixed') {
        counts.mixed++
        counts.total++
      } else if (type === 'wild') {
        counts.wild++
        counts.total++
      }
    })

    return counts
  }

  /**
   * Check if team can go out (has required canastas)
   * @param melds - Team's melds
   * @param hiddenWildCanastas - Count of hidden wild canastas
   * @returns True if team has 2 natural + 2 mixed canastas
   */
  function canGoOut(
    melds: Meld[],
    hiddenWildCanastas = 0
  ): boolean {
    const counts = countCanastas(melds)
    // Going out requires all hidden canastas to be played
    return counts.natural >= 2 && counts.mixed >= 2 && hiddenWildCanastas === 0
  }

  // ============================================================================
  // Utility Functions
  // ============================================================================

  /**
   * Get the rank of cards in a meld (first natural card's rank)
   * @param cards - Cards in meld
   * @returns Rank or null if no natural cards
   */
  function getMeldRank(cards: Card[]): Rank | null {
    const natural = cards.find((c) => !isWild(c))
    return natural?.rank || null
  }

  /**
   * Group cards by rank
   * @param cards - Cards to group
   * @returns Map of rank to cards
   */
  function groupCardsByRank(cards: Card[]): Map<Rank, Card[]> {
    const groups = new Map<Rank, Card[]>()
    cards.forEach((card) => {
      const rank = card.rank
      if (!groups.has(rank)) {
        groups.set(rank, [])
      }
      groups.get(rank)!.push(card)
    })
    return groups
  }

  /**
   * Find potential melds in a set of cards
   * Returns suggested melds that combine 2+ natural cards with available wilds
   * @param cards - Cards to analyze (typically player's hand)
   * @param existingMelds - Map of existing team melds (rank -> card count) to suggest additions
   * @returns Array of potential meld suggestions
   */
  interface MeldSuggestion {
    rank: Rank
    naturals: Card[]
    wilds: Card[]
    totalCards: number
    points: number
    isNewMeld: boolean
    canFormCanasta: boolean
  }

  function findPotentialMelds(cards: Card[], existingMelds?: Map<string, number>): MeldSuggestion[] {
    const suggestions: MeldSuggestion[] = []

    // Separate natural cards and wild cards
    const naturals = cards.filter(c => isNatural(c))
    const wilds = cards.filter(c => isWild(c))

    // Group naturals by rank
    const naturalGroups = groupCardsByRank(naturals)

    // For each rank with 2+ naturals, create a potential meld suggestion
    naturalGroups.forEach((rankCards, rank) => {
      // Skip dead cards (3s)
      if (rank === '3') return

      const naturalCount = rankCards.length
      const existingCount = existingMelds?.get(rank) || 0
      const isNewMeld = existingCount === 0

      // Need at least 2 naturals for a new meld, or 1 to add to existing
      if (isNewMeld && naturalCount < 2) return
      if (!isNewMeld && naturalCount < 1) return

      // Calculate how many wilds we can use (max 3 for new meld, varies for existing)
      const maxWilds = isNewMeld ? Math.min(naturalCount - 1, 3) : 3 - Math.floor(existingCount / 2)
      const wildsToUse = Math.min(wilds.length, Math.max(0, maxWilds))

      // Only suggest if we have at least 3 cards total for new meld
      const totalCards = naturalCount + wildsToUse + existingCount
      if (isNewMeld && totalCards < 3) return

      const meldCards = [...rankCards, ...wilds.slice(0, wildsToUse)]
      const points = meldCards.reduce((sum, c) => sum + getCardPoints(c), 0)

      suggestions.push({
        rank,
        naturals: rankCards,
        wilds: wilds.slice(0, wildsToUse),
        totalCards,
        points,
        isNewMeld,
        canFormCanasta: totalCards >= CANASTA_SIZE,
      })
    })

    // Sort by points (highest first), then by whether it can form a canasta
    suggestions.sort((a, b) => {
      if (a.canFormCanasta !== b.canFormCanasta) return a.canFormCanasta ? -1 : 1
      return b.points - a.points
    })

    return suggestions
  }

  /**
   * Sort cards by rank and suit
   * @param cards - Cards to sort
   * @returns Sorted array of cards
   */
  function sortCards(cards: Card[]): Card[] {
    const rankOrder: Record<Rank, number> = {
      JOKER: 0,
      '2': 1,
      A: 2,
      K: 3,
      Q: 4,
      J: 5,
      '10': 6,
      '9': 7,
      '8': 8,
      '7': 9,
      '6': 10,
      '5': 11,
      '4': 12,
      '3': 13,
    }

    const suitOrder: Record<Suit, number> = {
      hearts: 0,
      diamonds: 1,
      clubs: 2,
      spades: 3,
      joker: 4,
    }

    return [...cards].sort((a, b) => {
      const rankDiff = rankOrder[a.rank] - rankOrder[b.rank]
      if (rankDiff !== 0) return rankDiff
      return suitOrder[a.suit] - suitOrder[b.suit]
    })
  }

  // ============================================================================
  // Return API
  // ============================================================================

  return {
    // Classification
    isWild,
    isNatural,
    isDead,
    isRedThree,
    isBlackThree,
    getCardType,

    // Points
    getCardPoints,
    calculateTotalPoints,
    getOpeningMeldRequirement,

    // Meld validation
    validateMeld,
    canAddToMeld,

    // Canasta detection
    isCanasta,
    getCanastaType,
    getCanastaBonus,
    countCanastas,
    canGoOut,

    // Utilities
    getMeldRank,
    groupCardsByRank,
    sortCards,
    findPotentialMelds,

    // Constants (exported for convenience)
    OPENING_MELD_REQUIREMENTS,
    CANASTA_BONUS,
    CANASTA_SIZE,
    MIN_MELD_SIZE,
    MIN_NATURAL_CARDS,
    MAX_WILD_CARDS_BEFORE_CANASTA,
  }
}
